import React, { useEffect, useState } from 'react';
import { archiveAPI } from '../../api/archive';
import { Archive, RotateCcw, Eye, Search } from 'lucide-react';
import { toast } from 'sonner';

const TABS = [
  { id: 'clients', label: 'Clientes', entity: 'clients' },
  { id: 'projects', label: 'Proyectos', entity: 'projects' },
  { id: 'events', label: 'Eventos', entity: 'events' },
  { id: 'evaluations', label: 'Evaluaciones', entity: 'evaluations' },
  { id: 'locations', label: 'Locaciones', entity: 'locations' },
  { id: 'users', label: 'Usuarios', entity: 'users' },
  { id: 'staff', label: 'Personal', entity: 'staff' },
  { id: 'templates', label: 'Plantillas', entity: 'templates' },
];

export default function ArchivoPage() {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('clients');
  const [confirmModal, setConfirmModal] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await archiveAPI.getAll();
      setData(r.data || {});
    } catch {
      // API may not exist yet — degrade gracefully
      setData({});
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleUnarchive = async (entity, id) => {
    setConfirmModal(null);
    try {
      await archiveAPI.unarchive(entity, id);
      toast.success('Elemento reactivado correctamente');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al reactivar');
    }
  };

  const items = data[activeTab] || [];
  const tabConfig = TABS.find(t => t.id === activeTab);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="archivo-page">
      <div>
        <h1 className="text-2xl font-bold text-soem-navy">Archivo</h1>
        <p className="text-slate-500 text-sm mt-1">Elementos archivados del sistema</p>
      </div>

      <div className="flex gap-1.5 flex-wrap border-b border-slate-200 pb-0">
        {TABS.map(tab => {
          const count = (data[tab.id] || []).length;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-2 text-xs font-semibold rounded-t-sm border-b-2 transition-all ${activeTab === tab.id
                ? 'border-soem-primary text-soem-primary bg-blue-50'
                : 'border-transparent text-slate-400 hover:text-slate-600'}`}
              data-testid={`tab-${tab.id}`}>
              {tab.label} ({count})
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="soem-card h-14 animate-pulse bg-slate-100" />)}</div>
      ) : items.length === 0 ? (
        <div className="soem-card p-12 text-center">
          <Archive size={40} className="text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No hay {tabConfig?.label.toLowerCase()} archivados.</p>
        </div>
      ) : (
        <div className="soem-card overflow-hidden">
          <table className="w-full text-sm" data-testid="archive-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-3 font-semibold text-soem-navy text-xs uppercase tracking-wide">Nombre</th>
                <th className="text-left px-4 py-3 font-semibold text-soem-navy text-xs uppercase tracking-wide">Fecha archivado</th>
                <th className="text-left px-4 py-3 font-semibold text-soem-navy text-xs uppercase tracking-wide">Archivado por</th>
                <th className="text-right px-4 py-3 font-semibold text-soem-navy text-xs uppercase tracking-wide">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map(item => (
                <tr key={item.id} className="hover:bg-slate-50 transition-colors" data-testid={`archived-${item.id}`}>
                  <td className="px-4 py-3 font-medium text-soem-navy">{item.name || item.title || item.first_name || 'Sin nombre'}</td>
                  <td className="px-4 py-3 text-slate-500">{item.archived_at ? new Date(item.archived_at).toLocaleDateString('es-MX') : '—'}</td>
                  <td className="px-4 py-3 text-slate-500">{item.archived_by_name || item.archived_by || '—'}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => setConfirmModal({ entity: tabConfig.entity, id: item.id, name: item.name || item.title || '' })}
                        className="soem-btn-primary px-3 py-1.5 text-xs flex items-center gap-1" data-testid={`unarchive-${item.id}`}>
                        <RotateCcw size={12} /> Reactivar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {confirmModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-sm shadow-xl w-full max-w-sm animate-fade-in p-6">
            <h3 className="font-bold text-soem-navy mb-2">Reactivar elemento</h3>
            <p className="text-sm text-slate-500 mb-4">
              ¿Reactivar <strong>{confirmModal.name}</strong>? Volvera a aparecer en la lista principal.
            </p>
            <div className="flex gap-3">
              <button onClick={() => handleUnarchive(confirmModal.entity, confirmModal.id)}
                className="soem-btn-primary flex-1 py-2 text-sm" data-testid="confirm-unarchive">
                Reactivar
              </button>
              <button onClick={() => setConfirmModal(null)} className="soem-btn-secondary flex-1 py-2 text-sm">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
